/**
 * Quote coefficients, levels (偏差 D8, JF 2026-09-29; on the server and per quantity since JF 2026-10-13):
 *   1. the company cost-sheet template (DEFAULT_QCOEF)
 *   2. company defaults an admin sets in the admin page — a base set and one per order quantity
 *   3. "my defaults" — each person's own, a base set and one per order quantity (on the server, they follow the person)
 *   4. per sheet (sheet.qcoef, per quantity in a tiered quote) — and each line / CM / additional can still be overridden by hand
 * For a quantity: my set for it → the company's for it → my base → the company base → the template.
 * "For it" = the largest saved quantity not above it; if neither mine nor the company's has one, the smallest saved.
 */
import { DEFAULT_QCOEF, applyCoefPrefs, baseCoef, coefAt, coefFor, hasQtyCoef, type QCoef, type Sheet } from "@ic/engine";
import { useStore, type CoefPrefs } from "../../store.ts";
import { api } from "../../shared/api.ts";
import { KEYS, lsDel, lsGet } from "../../shared/storage.ts";

const prefs = () => useStore.getState().coefPrefs;
const full = (c: Partial<QCoef>): QCoef => ({ ...DEFAULT_QCOEF, ...c });

// the rules themselves are in the engine (prefs.ts): the server's estimate jobs use them too (JF 2026-10-16)
/** My base defaults (else the company base, else the template). */
export const myCoef = (): QCoef => baseCoef(prefs());

/** The defaults for one order quantity. A set saved at or below it (mine first) beats the smallest-saved fallback. */
export const myCoefFor = (qty: number | undefined): QCoef => coefFor(prefs(), qty);

/** The company defaults (set in the admin page) for a quantity, else the company base, else the template. */
export function companyCoefFor(qty: number | undefined): QCoef {
  const co = prefs()?.company;
  return full((qty ? coefAt(co?.byQty, qty) : null) || co?.base || {});
}

/** Are there defaults for particular quantities (mine or the company's)? */
export const hasQtyDefaults = (): boolean => hasQtyCoef(prefs());

function saveMine(next: CoefPrefs) {
  const st = useStore.getState();
  st.setCoefPrefs({ mine: next, company: st.coefPrefs?.company ?? null });
  return api.saveMyCoef(next);
}
export const saveMyCoef = (c: QCoef) => saveMine({ ...(prefs()?.mine || {}), base: c });
export const saveMyCoefFor = (qty: number, c: QCoef) => {
  const mine = prefs()?.mine || {};
  return saveMine({ ...mine, byQty: { ...(mine.byQty || {}), [String(qty)]: c } });
};
export const resetMyCoef = () => saveMine({ ...(prefs()?.mine || {}), base: null });

/**
 * After login: load the defaults from the server. Defaults this browser kept before they moved to the server are uploaded
 * once (when the server has none for this person) and then removed from the browser.
 */
export async function loadCoefPrefs(): Promise<void> {
  const r = await api.coefPrefs();
  let mine = r.mine;
  const oldBase = lsGet<Partial<QCoef> | null>(KEYS.qcoef, null);
  const oldQty = lsGet<Record<string, QCoef> | null>(KEYS.qcoefQty, null);
  const empty = !mine?.base && !Object.keys(mine?.byQty || {}).length;
  if (empty && (oldBase || oldQty)) {
    mine = { ...(mine || {}), ...(oldBase ? { base: full(oldBase) } : {}), ...(oldQty ? { byQty: oldQty } : {}) };
    await api.saveMyCoef(mine);
  }
  if (oldBase || oldQty) { lsDel(KEYS.qcoef); lsDel(KEYS.qcoefQty); }
  useStore.getState().setCoefPrefs({ mine: mine ?? null, company: r.company ?? null });
  if (r.fx) useStore.getState().setDefaultFx(r.fx);
}

/**
 * A new AI sheet starts with my defaults for its quantity (or each quantity of a tiered quote) — mine, else the company's.
 * Mutates and returns the sheet.
 */
export const withMyDefaults = (sheet: Sheet): Sheet => applyCoefPrefs(sheet, prefs());

/** Editable coefficients: [key, English, 中文, kind]. "pct" is shown and typed in %, "x" as a factor. */
export const COEF_FIELDS: [keyof QCoef, string, string, "pct" | "x"][] = [
  ["fabPx", "Fabric price +", "面料单价 +", "pct"],
  ["fabYld", "Fabric yield +", "面料用量 +", "pct"],
  ["trimPx", "Trim price +", "辅料单价 +", "pct"],
  ["trimYld", "Trim yield +", "辅料用量 +", "pct"],
  ["finActual", "Finance (actual)", "成本 Finance", "pct"],
  ["fin", "Finance (quoted)", "报价 Finance", "pct"],
  ["add", "Additional +", "外部费用 +", "pct"],
  ["cmMult", "CM ×", "CM 倍数 ×", "x"],
];
